import { App as AntApp, Button, Form, Input, Modal } from "antd";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Mail, Phone, ShieldCheck, UserRound } from "lucide-react";
import { useState } from "react";

interface ScheduleDemoModalProps {
  open: boolean;
  onCancel: () => void;
}

interface DemoFormValues {
  name: string;
  email: string;
  phone: string;
  company?: string;
  message?: string;
}

/**
 * Modal for scheduling a demo with form for client details
 * @param open Whether the modal is visible
 * @param onCancel Callback to close the modal
 */
export default function ScheduleDemoModal({ open, onCancel }: ScheduleDemoModalProps) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const inputClassName =
    "!rounded-lg !border-gray-200 !bg-[#F9FAFB] !text-[#131B2A] !shadow-none hover:!border-(--brand-primary) focus:!border-(--brand-primary) focus:!shadow-none";

  /**
   * Handles demo request form submission
   */
  const handleSubmit = async (values: DemoFormValues) => {
    try {
      setLoading(true);
      await axios.post(`/api/demo/request`, values);
      message.success("Demo request submitted successfully! We'll be in touch soon.");
      form.resetFields();
      onCancel();
    } catch (error: unknown) {
      console.error("Error submitting demo request:", error);
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error || "Failed to submit demo request. Please try again."
        : "Failed to submit demo request. Please try again.";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={860}
      centered
      className="tripbox-lead-modal"
      styles={{
        content: {
          padding: 0,
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid #E5E7EB",
        },
        body: {
          padding: 0,
        },
      }}
    >
      <div className="grid bg-white lg:grid-cols-[0.9fr_1.1fr]">
        <aside className="relative overflow-hidden bg-[#131B2A] p-7 text-white sm:p-8">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-45"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-lg border border-(--brand-primary)/35 bg-(--brand-primary)/10 px-3 py-2 text-xs font-semibold text-(--brand-primary)">
              <ShieldCheck className="h-4 w-4" />
              TripboxCRM quote desk
            </div>

            <h2 className="mt-7 max-w-[12ch] text-4xl font-semibold leading-tight tracking-normal text-white">
              Get a CRM plan built around your travel workflow.
            </h2>
            <p className="mt-5 max-w-sm text-sm leading-7 text-white/62">
              Share a few details and our team will map your lead, itinerary, operations, and collection workflow before
              preparing a custom quote.
            </p>

            <div className="mt-8 space-y-3">
              {[
                "Workflow review for your agency or DMC",
                "Pricing based on team size and modules",
                "Implementation guidance for your current process",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-(--brand-primary)" />
                  <span className="text-sm leading-6 text-white/78">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <section className="p-6 sm:p-8">
          <div className="border-b border-gray-200 pb-5">
            <p className="text-sm font-semibold text-(--brand-primary)">Request custom quote</p>
            <h3 className="mt-2 text-2xl font-semibold leading-tight tracking-normal text-[#131B2A]">
              Tell us about your travel business.
            </h3>
            <p className="mt-3 text-sm leading-6 text-gray-500">
              We usually respond within one business day with next steps for a walkthrough and quote.
            </p>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
            className="mt-6 [&_.ant-form-item-label>label]:!font-semibold [&_.ant-form-item-label>label]:!text-[#131B2A] [&_.ant-form-item]:!mb-4"
          >
            <div className="grid gap-x-4 sm:grid-cols-2">
              <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter your name" }]}>
                <Input
                  prefix={<UserRound className="mr-1 h-4 w-4 text-gray-400" />}
                  placeholder="John Doe"
                  size="large"
                  className={inputClassName}
                />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  prefix={<Mail className="mr-1 h-4 w-4 text-gray-400" />}
                  placeholder="john@example.com"
                  size="large"
                  className={inputClassName}
                />
              </Form.Item>

              <Form.Item
                label="Phone"
                name="phone"
                rules={[{ required: true, message: "Please enter your phone number" }]}
              >
                <Input
                  prefix={<Phone className="mr-1 h-4 w-4 text-gray-400" />}
                  placeholder="+91 98765 43210"
                  size="large"
                  className={inputClassName}
                />
              </Form.Item>

              <Form.Item label="Company" name="company">
                <Input placeholder="Your company name" size="large" className={inputClassName} />
              </Form.Item>
            </div>

            <Form.Item label="Message" name="message">
              <Input.TextArea
                placeholder="Tell us about your team size, destinations, and current CRM setup..."
                rows={4}
                size="large"
                className={inputClassName}
              />
            </Form.Item>

            <Form.Item className="mb-0!">
              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end">
                <Button onClick={onCancel} size="large" className="!h-11 !rounded-lg !border-gray-200 !font-semibold">
                  Cancel
                </Button>
                <Button
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  className="!h-11 !rounded-lg !border-(--brand-secondary) !bg-(--brand-secondary) !px-5 !font-semibold !text-[var(--brand-secondary-foreground)] !shadow-none hover:!border-(--brand-secondary-hover) hover:!bg-(--brand-secondary-hover)"
                >
                  Submit Request
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </Form.Item>
          </Form>
        </section>
      </div>
    </Modal>
  );
}
